import { useState, useEffect, useCallback, useMemo } from "react";
import { Bell, RefreshCw, Smartphone, Send, CheckCheck, Clock, FileText, FileDown } from "lucide-react";
import { toast } from "sonner";
import api, { API, dlToken } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const TYPE_LABELS = { manual: "Ręczne", fault: "Awaria", awaria: "Awaria", prace: "Prace planowe", invoice: "Faktura", contract: "Umowa", info: "Info" };
const TYPE_COLORS = { manual: "text-slate-300", fault: "text-red-400", invoice: "text-amber-400", contract: "text-cyan-400", info: "text-slate-400" };
const CHART_TYPES = [
  { key: "fault", label: "Awaria", bg: "bg-red-500" },
  { key: "contract", label: "Umowa", bg: "bg-cyan-400" },
  { key: "invoice", label: "Faktura", bg: "bg-amber-400" },
  { key: "manual", label: "Ręczne", bg: "bg-slate-400" },
];

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="flex-1 min-w-[130px] rounded-md border border-slate-800 bg-slate-800/40 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase text-slate-500"><Icon size={12} className={color} /> {label}</div>
      <div className="text-xl font-bold text-slate-100 mt-0.5">{value}</div>
      {sub ? <div className="text-[11px] text-slate-500">{sub}</div> : null}
    </div>
  );
}

function isoDaysAgo(n) {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
}

export function NotificationsPanel({ t }) {
  const { settings, save } = useSettings();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [fClient, setFClient] = useState("");
  const [fType, setFType] = useState("");
  const [dFrom, setDFrom] = useState(isoDaysAgo(30));
  const [dTo, setDTo] = useState(new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(settings.alert_days_before ?? 7);
  const [savingDays, setSavingDays] = useState(false);

  const qs = useCallback(() => {
    const p = [];
    if (fClient) p.push(`client_id=${fClient}`);
    if (fType) p.push(`type=${fType}`);
    if (dFrom) p.push(`date_from=${dFrom}`);
    if (dTo) p.push(`date_to=${dTo}`);
    return p.length ? "?" + p.join("&") : "";
  }, [fClient, fType, dFrom, dTo]);

  const load = useCallback(() => {
    setBusy(true);
    api.get(`/push/log${qs()}`).then((r) => setRows(r.data || [])).catch(() => setRows([])).finally(() => setBusy(false));
  }, [qs]);
  const loadStats = useCallback(() => {
    api.get("/push/stats").then((r) => setStats(r.data)).catch(() => setStats(null));
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data || [])).catch(() => { }); }, []);

  const saveDays = async () => {
    setSavingDays(true);
    try {
      const v = Math.max(1, Number(days) || 7);
      await save({ alert_days_before: v });
      setDays(v);
      toast.success(`${t("Zapisano próg alertów")}: ${v} ${t("dni")}`);
    } catch { toast.error(t("Nie udało się zapisać")); }
    finally { setSavingDays(false); }
  };

  const token = dlToken();
  const exportReport = (fmt) => {
    const p = [`format=${fmt}`];
    if (fClient) p.push(`client_id=${fClient}`);
    if (fType) p.push(`type=${fType}`);
    if (dFrom) p.push(`date_from=${dFrom}`);
    if (dTo) p.push(`date_to=${dTo}`);
    if (token) p.push(`auth=${token}`);
    window.open(`${API}/push/log/export?${p.join("&")}`, "_blank");
  };

  const reach = stats ? stats.reach_pct : 0;

  const chart = useMemo(() => {
    const map = {};
    (rows || []).forEach((r) => {
      const d = String(r.created_at || "").slice(0, 10);
      if (!d) return;
      const tp = TYPE_LABELS[r.type] ? r.type : "manual";
      if (!map[d]) map[d] = { manual: 0, contract: 0, invoice: 0, fault: 0, total: 0 };
      map[d][tp] = (map[d][tp] || 0) + 1;
      map[d].total += 1;
    });
    const dayKeys = Object.keys(map).sort().slice(-14);
    const max = Math.max(1, ...dayKeys.map((d) => map[d].total));
    return { days: dayKeys.map((d) => ({ date: d, ...map[d] })), max };
  }, [rows]);

  return (
    <div data-testid="notifications-panel">
      <div className="flex items-center gap-2 mb-3">
        <Bell size={16} className="text-indigo-400" />
        <span className="text-sm font-semibold text-slate-200">{t("Powiadomienia i zasięg")}</span>
        <button onClick={() => { load(); loadStats(); }} data-testid="notifications-refresh" className="ml-auto text-slate-400 hover:text-white p-1 rounded" title={t("Odśwież")}>
          <RefreshCw size={14} className={busy ? "animate-spin" : ""} />
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3" data-testid="notifications-stats">
        <StatCard icon={Smartphone} color="text-emerald-400" label={t("Zasięg (z aplikacją)")}
          value={stats ? `${stats.clients_with_app}/${stats.total_clients}` : "—"} sub={stats ? `${reach}% ${t("klientów")}` : ""} />
        <StatCard icon={Smartphone} color="text-slate-500" label={t("Bez aplikacji")}
          value={stats ? stats.clients_without_app : "—"} sub={t("nie otrzyma push")} />
        <StatCard icon={Send} color="text-indigo-400" label={t("Wysłane")}
          value={stats ? stats.total_notifications : "—"} sub={t("powiadomień")} />
        <StatCard icon={CheckCheck} color="text-cyan-400" label={t("Dostarczone")}
          value={stats ? stats.total_delivered : "—"} sub={t("wysyłek push")} />
      </div>
      <div className="mb-4">
        <div className="h-2 w-full rounded-full bg-slate-800 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-cyan-400 transition-all" style={{ width: `${reach}%` }} data-testid="reach-bar" />
        </div>
        <div className="text-[11px] text-slate-500 mt-1">{t("Zasięg powiadomień push")}: {reach}%</div>
      </div>

      {/* Wykres wg typu w czasie */}
      <div className="mb-4 rounded-md border border-slate-800 bg-slate-800/40 px-3 py-3" data-testid="notifications-chart">
        <div className="text-[10px] uppercase text-slate-500 mb-2">{t("Powiadomienia wg typu (w czasie)")}</div>
        {chart.days.length === 0 ? (
          <div className="text-xs text-slate-500 py-2">{t("Brak danych w wybranym okresie")}</div>
        ) : (
          <>
            <div className="flex items-end gap-1.5 h-24">
              {chart.days.map((d) => (
                <div key={d.date} className="flex-1 flex flex-col justify-end items-center group relative" data-testid={`chart-day-${d.date}`}>
                  <div className="w-full flex flex-col-reverse rounded-sm overflow-hidden" style={{ height: `${(d.total / chart.max) * 100}%`, minHeight: d.total ? "3px" : "0" }}>
                    {CHART_TYPES.map((ct) => d[ct.key] > 0 && (
                      <div key={ct.key} className={ct.bg} style={{ flexGrow: d[ct.key] }} title={`${ct.label}: ${d[ct.key]}`} />
                    ))}
                  </div>
                  <div className="absolute -top-5 hidden group-hover:block text-[10px] bg-slate-900 border border-slate-700 rounded px-1 whitespace-nowrap">{d.total}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3 mt-2">
              {CHART_TYPES.map((ct) => (
                <div key={ct.key} className="flex items-center gap-1 text-[10px] text-slate-400"><span className={`w-2.5 h-2.5 rounded-sm ${ct.bg}`} /> {t(ct.label)}</div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Próg alertów */}
      <div className="mb-4 rounded-md border border-slate-800 bg-slate-800/40 px-3 py-2.5 flex flex-wrap items-center gap-2" data-testid="alert-threshold-box">
        <Clock size={14} className="text-amber-400" />
        <span className="text-sm text-slate-300">{t("Alert o końcu umowy / gwarancji na")}</span>
        <input type="number" min="1" max="365" value={days} onChange={(e) => setDays(e.target.value)}
          data-testid="alert-days-input" className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-slate-200" />
        <span className="text-sm text-slate-300">{t("dni przed")}</span>
        <button onClick={saveDays} disabled={savingDays} data-testid="alert-days-save"
          className="ml-auto bg-cyan-700 hover:bg-cyan-600 disabled:opacity-50 rounded px-3 py-1.5 text-xs font-medium">
          {savingDays ? t("Zapisywanie…") : t("Zapisz")}
        </button>
      </div>

      {/* Filtry + okres + eksport */}
      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div>
          <label className="text-[10px] text-slate-500 uppercase block">{t("Od")}</label>
          <input type="date" value={dFrom} onChange={(e) => setDFrom(e.target.value)} data-testid="notifications-date-from"
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
        </div>
        <div>
          <label className="text-[10px] text-slate-500 uppercase block">{t("Do")}</label>
          <input type="date" value={dTo} onChange={(e) => setDTo(e.target.value)} data-testid="notifications-date-to"
            className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200" />
        </div>
        <select value={fClient} onChange={(e) => setFClient(e.target.value)} data-testid="notifications-filter-client"
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200">
          <option value="">{t("Wszyscy klienci")}</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name || c.address || c.id}</option>)}
        </select>
        <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="notifications-filter-type"
          className="bg-slate-900 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-200">
          <option value="">{t("Wszystkie typy")}</option>
          <option value="manual">{t("Ręczne")}</option>
          <option value="contract">{t("Umowa")}</option>
          <option value="invoice">{t("Faktura")}</option>
          <option value="fault">{t("Awaria")}</option>
          <option value="awaria">{t("Awaria (masowe)")}</option>
          <option value="prace">{t("Prace planowe")}</option>
        </select>
        <button onClick={() => exportReport("csv")} data-testid="notifications-export-csv"
          className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1.5 text-xs flex items-center gap-1"><FileText size={13} /> CSV</button>
        <button onClick={() => exportReport("pdf")} data-testid="notifications-export-pdf"
          className="bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1.5 text-xs flex items-center gap-1"><FileDown size={13} /> PDF</button>
      </div>

      {rows.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center">{t("Brak wysłanych powiadomień")}</div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-slate-800">
          <table className="w-full text-sm" data-testid="notifications-table">
            <thead className="bg-slate-800/60 text-[11px] uppercase text-slate-400">
              <tr>
                <th className="text-left px-3 py-2">{t("Kiedy")}</th>
                <th className="text-left px-3 py-2">{t("Odbiorca")}</th>
                <th className="text-left px-3 py-2">{t("Typ")}</th>
                <th className="text-left px-3 py-2">{t("Treść")}</th>
                <th className="text-left px-3 py-2">{t("Dostarczone")}</th>
                <th className="text-left px-3 py-2">{t("Kto")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`notification-row-${r.id}`} className="border-t border-slate-800 hover:bg-slate-800/40">
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{String(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 text-slate-200">{r.client_name || (r.broadcast ? t("Wszyscy klienci") : "—")}</td>
                  <td className={`px-3 py-2 font-medium ${TYPE_COLORS[r.type] || "text-slate-300"}`}>{t(TYPE_LABELS[r.type] || r.type || "—")}</td>
                  <td className="px-3 py-2 text-slate-300 max-w-[280px]"><div className="font-medium truncate">{r.title}</div><div className="text-xs text-slate-500 truncate">{r.body}</div></td>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{r.sent}/{r.recipients}{(r.email_sent || r.sms_sent) ? <span className="ml-1 text-[10px] text-slate-500">(✉{r.email_sent || 0} · ✆{r.sms_sent || 0})</span> : null}</td>
                  <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{r.by || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
